/* ════════════════════════════════════════════════════════════
   VELVET RITUALS — Spotlight search behaviour
   assets/vr-spotlight.js

   One global search. Replaces the theme's search-drawer front
   door (click on any [aria-controls="search-drawer"] trigger is
   intercepted; ⌘K / Ctrl+K / "/" also open it). Underneath it is
   Shopify's Predictive Search API, upgraded three ways:

   1. Intent layer — the query is normalised through a synonym /
      phrase map (wellness vocabulary, Hinglish spellings) before
      it hits the API, and result sets for all variants merge.
   2. Relaxation ladder — zero results never dead-ends: variants,
      then dropped tokens, then per-token queries, then a typo
      trim run in turn; the first hit renders under a
      "closest matches" note.
   3. Grounded fallback — if everything misses, curated quick
      links and popular products render instead of a blank panel.

   The /search results page and the theme's product search stay
   exactly as they are. Self-guards for multiple includes.
   ════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  if (window.__vrSpotlightInit) return;
  window.__vrSpotlightInit = true;

  var ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  ready(function () {
    var root = document.getElementById('vr-spotlight');
    if (!root) return;

    var input = root.querySelector('.vrsl__input');
    var body = root.querySelector('[data-vrsl-body]');
    var listbox = root.querySelector('[data-vrsl-results]');
    var zero = root.querySelector('[data-vrsl-zero]');
    var searchUrl = root.getAttribute('data-search-url') || '/search';
    var suggestUrl = (root.getAttribute('data-suggest-url') || '/search/suggest') + '.json';

    /* ── Intent layer: synonyms & phrases ──────────────── */
    // phrase → replacement (checked first, longest wins)
    var PHRASES = {
      'sex toy': 'massager', 'sex toys': 'massager', 'adult toy': 'massager',
      'first time': 'first', 'long distance': 'long-distance',
      'how to clean': 'clean', 'for couples': 'couples', 'for men': 'men', 'for women': 'women'
    };
    // token → alternates tried as extra queries
    var SYNONYMS = {
      lube: ['lubricant'], lubricant: ['lube'],
      vibrator: ['massager'], massager: ['vibrator'],
      toy: ['massager'], toys: ['massager'],
      vibe: ['massager'], dildo: ['massager'],
      cleaner: ['clean'], cleaning: ['clean'],
      beginner: ['first'], beginners: ['first'],
      gift: ['bundle'], combo: ['bundle'],
      cheap: ['bundle'], affordable: ['bundle'],
      orgasm: ['climax'], climax: ['orgasm'],
      exercise: ['kegel'], quiet: ['discreet'], silent: ['discreet']
    };

    var variants = function (q) {
      var out = [];
      var norm = ' ' + q.toLowerCase().trim().replace(/\s+/g, ' ') + ' ';
      Object.keys(PHRASES).forEach(function (ph) {
        if (norm.indexOf(' ' + ph + ' ') !== -1) {
          norm = norm.split(' ' + ph + ' ').join(' ' + PHRASES[ph] + ' ');
        }
      });
      norm = norm.trim();
      if (norm && norm !== q.toLowerCase().trim()) out.push(norm);
      var tokens = norm.split(' ');
      tokens.forEach(function (t, i) {
        (SYNONYMS[t] || []).forEach(function (alt) {
          var v = tokens.slice();
          v[i] = alt;
          out.push(v.join(' '));
        });
      });
      return out.slice(0, 3);
    };

    /* ── Predictive fetch ──────────────────────────────── */
    var fetchSuggest = function (q) {
      var params = new URLSearchParams({ q: q });
      params.append('resources[type]', 'product,article,page,collection');
      params.append('resources[limit]', '5');
      params.append('resources[limit_scope]', 'each');
      params.append('resources[options][unavailable_products]', 'last');
      params.append('resources[options][fields]', 'title,body,product_type,variants.title,vendor,tag,author');
      return fetch(suggestUrl + '?' + params.toString())
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { return j && j.resources && j.resources.results ? j.resources.results : null; })
        .then(function (res) {
          // City Guides is an SEO-only surface, found by Google search, not this one.
          if (res && res.articles) res.articles = res.articles.filter(function (a) {
            return a.url.indexOf('/blogs/city-guides/') !== 0;
          });
          return res;
        })
        .catch(function () { return null; });
    };

    var countOf = function (res) {
      if (!res) return 0;
      return ['products', 'articles', 'pages', 'collections'].reduce(function (n, k) {
        return n + ((res[k] && res[k].length) || 0);
      }, 0);
    };

    var mergeInto = function (base, extra) {
      if (!extra) return base;
      ['products', 'articles', 'pages', 'collections'].forEach(function (k) {
        var seen = {};
        (base[k] = base[k] || []).forEach(function (it) { seen[it.url] = true; });
        (extra[k] || []).forEach(function (it) {
          if (!seen[it.url] && base[k].length < 5) { base[k].push(it); seen[it.url] = true; }
        });
      });
      return base;
    };

    /* ── Relaxation ladder ─────────────────────────────── */
    var relaxQueries = function (q) {
      var tokens = q.toLowerCase().trim().split(/\s+/).filter(function (t) { return t.length > 1; });
      var out = [];
      if (tokens.length > 1) {
        var shortest = tokens.reduce(function (a, b) { return b.length < a.length ? b : a; });
        out.push(tokens.filter(function (t) { return t !== shortest; }).join(' '));
        tokens.slice().sort(function (a, b) { return b.length - a.length; }).slice(0, 2)
          .forEach(function (t) { out.push(t); });
      }
      var longest = tokens.reduce(function (a, b) { return b.length > a.length ? b : a; }, '');
      if (longest.length > 4) out.push(longest.slice(0, longest.length - 2));
      return out.filter(function (v, i, a) { return v && v !== q && a.indexOf(v) === i; }).slice(0, 4);
    };

    /* ── Search orchestration ──────────────────────────── */
    var seq = 0;
    var runSearch = function (q) {
      var mySeq = ++seq;
      var exactP = fetchSuggest(q);
      var variantPs = variants(q).map(fetchSuggest);

      Promise.all([exactP].concat(variantPs)).then(function (all) {
        if (mySeq !== seq) return;
        var merged = all.filter(Boolean).reduce(mergeInto, {});
        if (countOf(merged) > 0) { render(merged, q, false); return; }

        // ladder — sequential, first hit wins
        var ladder = relaxQueries(q);
        var step = function (i) {
          if (mySeq !== seq) return;
          if (i >= ladder.length) { renderZero(q); return; }
          fetchSuggest(ladder[i]).then(function (res) {
            if (mySeq !== seq) return;
            if (countOf(res) > 0) render(res, q, true);
            else step(i + 1);
          });
        };
        step(0);
      });
    };

    /* ── Rendering ─────────────────────────────────────── */
    var esc = function (s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    };
    var hl = function (title, q) {
      var out = esc(title);
      q.toLowerCase().split(/\s+/).filter(function (t) { return t.length > 2; }).forEach(function (t) {
        var i = out.toLowerCase().indexOf(t);
        if (i > -1) out = out.slice(0, i) + '<mark>' + out.slice(i, i + t.length) + '</mark>' + out.slice(i + t.length);
      });
      return out;
    };
    var money = function (p) {
      if (p == null || p === '') return '';
      var n = parseFloat(p);
      if (isNaN(n)) return '';
      return '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: n % 1 === 0 ? 0 : 2 }).format(n);
    };
    var ICONS = {
      article: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4H6.5A2.5 2.5 0 0 0 4 6.5v13z"/><path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5"/></svg>',
      page: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/></svg>',
      collection: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>'
    };

    var itemHtml = function (it, kind, q) {
      var media = it.image || (it.featured_image && it.featured_image.url);
      var mediaHtml = media
        ? '<span class="vrsl__item-media"><img src="' + esc(media) + '" alt="" loading="lazy"></span>'
        : '<span class="vrsl__item-media">' + (ICONS[kind] || ICONS.page) + '</span>';
      var sub = '';
      var aside = '';
      if (kind === 'product') {
        aside = '<span class="vrsl__item-aside">' + money(it.price) + '</span>';
      } else if (kind === 'article') {
        sub = '<span class="vrsl__item-sub">' + esc((it.tags && it.tags[0]) || 'Article') + '</span>';
        aside = '<span class="vrsl__item-aside sub">Learn</span>';
      } else {
        aside = '<span class="vrsl__item-aside sub">' + (kind === 'collection' ? 'Collection' : 'Page') + '</span>';
      }
      return '<a class="vrsl__item" role="option" aria-selected="false" href="' + esc(it.url) + '">' +
        mediaHtml +
        '<span class="vrsl__item-body"><span class="vrsl__item-title">' + hl(it.title, q) + '</span>' + sub + '</span>' +
        aside + '</a>';
    };

    var render = function (res, q, approx) {
      var html = '';
      var groups = [
        ['products', 'Products'],
        ['articles', 'Articles'],
        ['collections', 'Collections'],
        ['pages', 'Pages']
      ];
      groups.forEach(function (g) {
        var items = res[g[0]] || [];
        if (!items.length) return;
        var note = approx && html === '' ? ' <span class="note">closest matches for “' + esc(q) + '”</span>' : '';
        html += '<div class="vrsl__group-label">' + g[1] + note + '</div>';
        items.forEach(function (it) {
          html += itemHtml(it, g[0].replace(/s$/, ''), approx ? '' : q);
        });
      });
      html += '<a class="vrsl__all" role="option" aria-selected="false" href="' +
        searchUrl + '?q=' + encodeURIComponent(q) + '">See all results for “' + esc(q) + '” →</a>';
      listbox.innerHTML = html;
      zero.hidden = true;
      listbox.hidden = false;
      active = -1;
      items = Array.prototype.slice.call(listbox.querySelectorAll('[role="option"]'));
    };

    var renderZero = function (q) {
      var note = root.querySelector('[data-vrsl-zero-note]');
      if (note) note.innerHTML = 'Nothing matched <strong>“' + esc(q) + '”</strong>. Here are some places to start instead.';
      listbox.innerHTML = '';
      listbox.hidden = true;
      zero.hidden = false;
      active = -1;
      items = Array.prototype.slice.call(zero.querySelectorAll('[role="option"]'));
    };

    var renderIdle = function () {
      listbox.innerHTML = '';
      listbox.hidden = true;
      zero.hidden = false;
      var note = root.querySelector('[data-vrsl-zero-note]');
      if (note) note.textContent = 'Search products, articles and guides in one box.';
      active = -1;
      items = Array.prototype.slice.call(zero.querySelectorAll('[role="option"]'));
    };

    /* ── Keyboard navigation ───────────────────────────── */
    var items = [];
    var active = -1;
    var setActive = function (i) {
      items.forEach(function (el, j) {
        el.setAttribute('aria-selected', j === i ? 'true' : 'false');
      });
      active = i;
      if (i > -1 && items[i]) items[i].scrollIntoView({ block: 'nearest' });
    };

    /* ── Open / close ──────────────────────────────────── */
    var isOpen = function () { return !root.hidden; };
    var open = function () {
      if (isOpen()) return;
      root.hidden = false;
      requestAnimationFrame(function () { root.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      renderIdle();
      input.value = '';
      setTimeout(function () { input.focus(); }, 30);
    };
    var close = function () {
      if (!isOpen()) return;
      root.classList.remove('is-open');
      document.body.style.overflow = '';
      seq++;
      var done = function () { root.hidden = true; };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
      else setTimeout(done, 300);
    };

    root.querySelectorAll('[data-vrsl-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    // Take over every trigger of the theme's search drawer, capture-phase
    // so the drawer never opens; the trigger href stays /search as a
    // no-JS fallback.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[aria-controls="search-drawer"]');
      if (!trigger) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open();
    }, true);

    document.addEventListener('keydown', function (e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        isOpen() ? close() : open();
        return;
      }
      if (e.key === '/' && !isOpen()) {
        var t = e.target;
        var typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
        if (!typing) { e.preventDefault(); open(); }
        return;
      }
      if (!isOpen()) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(Math.min(items.length - 1, active + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(Math.max(0, active - 1)); }
      else if (e.key === 'Enter') {
        if (active > -1 && items[active]) {
          e.preventDefault();
          items[active].click();
        }
        // else: let the form submit to /search?q=… as usual
      }
    });

    /* ── Input pipeline ────────────────────────────────── */
    var debounce;
    input.addEventListener('input', function () {
      clearTimeout(debounce);
      var q = input.value.trim();
      if (q.length < 2) { renderIdle(); return; }
      debounce = setTimeout(function () { runSearch(q); }, 180);
    });

    listbox.addEventListener('mousemove', function (e) {
      var el = e.target.closest('[role="option"]');
      if (el) setActive(items.indexOf(el));
    });
  });
})();
